import './index.scss';

import { Avatar, Button, Card, Divider, Tag } from 'antd';
import {
  FaCheckCircle,
  FaCreditCard,
  FaEdit,
  FaExclamationTriangle,
  FaKey,
  FaLock,
  FaPhone,
  FaPlus,
  FaShieldAlt,
  FaUniversity,
  FaUser,
  FaUserEdit,
  FaWallet,
} from 'react-icons/fa';

import ModalChangeBank from './component/ModalChangeBank';
import ModalChangePassword from './component/ModalChangePassword';
import ModalPasswordV2 from './component/ModalPasswordV2';
import { NS } from '~/utils/ns';
import { useState } from 'react';
import { useStateContext } from '~/contexts/ContextProvider';
import { useTranslation } from 'react-i18next';

const Profile = ({ title }) => {
  const { agency, formatMoney } = useStateContext();
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation([NS['profile']]);
  document.title = title;

  const getInitials = (username) => {
    return username ? username.charAt(0).toUpperCase() : 'U';
  };

  return (
    <div className="min-h-screen p-2 md:p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="mx-auto">
        {/* Header Section */}
        <div className="p-6 mb-8 border shadow-xl rounded-2xl bg-white/90 backdrop-blur-2xl border-white/20">
          <div className="flex items-center gap-4 mb-4">
            <Avatar
              size={80}
              className="text-2xl font-bold text-white shadow-lg bg-gradient-to-r from-blue-500 to-indigo-600">
              {getInitials(agency?.username)}
            </Avatar>
            <div>
              <h1 className="mb-2 text-3xl font-bold text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text">
                {t('thong_tin_tai_khoan')}
              </h1>
              <p className="text-gray-600">{t('quan_ly_thong_tin_ca_nhan_va_bao_mat_tai_khoan')}</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Profile Information Card */}
          <Card
            className="transition-all duration-300 border-0 shadow-xl rounded-2xl bg-white/90 backdrop-blur-sm hover:shadow-2xl"
            title={
              <div className="flex items-center gap-3 text-lg font-bold text-gray-800">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FaUser className="text-blue-600" />
                </div>
                {t('profile')}
              </div>
            }>
            <div className="space-y-4">
              {/* Username */}
              <div className="flex items-center gap-3 p-3 transition-colors bg-gray-50 rounded-xl hover:bg-gray-100">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FaUser className="text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="mb-1 text-xs text-gray-500">{t('account')}</div>
                  <div className="text-sm font-semibold text-gray-800">{agency?.username}</div>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-3 p-3 transition-colors bg-gray-50 rounded-xl hover:bg-gray-100">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FaPhone className="text-green-600" />
                </div>
                <div className="flex-1">
                  <div className="mb-1 text-xs text-gray-500">{t('phone')}</div>
                  <div className="text-sm font-semibold text-gray-800">{agency?.phone}</div>
                </div>
              </div>

              {/* Balance */}
              <div className="flex items-center gap-3 p-3 border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FaWallet className="text-green-600" />
                </div>
                <div className="flex-1">
                  <div className="mb-1 text-xs text-gray-500">{t('sodu')}</div>
                  <div className="text-lg font-bold text-green-600">
                    {formatMoney(agency?.money || 0)}
                  </div>
                </div>
                <Tag color="success" className="text-xs font-medium">
                  <FaCheckCircle className="inline mr-1" />
                  {t('hoat_dong')}
                </Tag>
              </div>
            </div>
          </Card>

          {/* Security Settings Card */}
          <Card
            className="transition-all duration-300 border-0 shadow-xl rounded-2xl bg-white/90 backdrop-blur-sm hover:shadow-2xl"
            title={
              <div className="flex items-center gap-3 text-lg font-bold text-gray-800">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <FaShieldAlt className="text-orange-600" />
                </div>
                {t('baomattaikhoan')}
              </div>
            }>
            <div className="space-y-4">
              {/* Login Password */}
              <div className="p-3 transition-colors bg-gray-50 rounded-xl hover:bg-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <FaLock className="text-red-600" />
                  </div>
                  <div className="flex-1">
                    <div className="mb-1 text-xs text-gray-500">{t('matkhaudangnhap')}</div>
                    <div className="text-sm font-semibold text-gray-800">***********</div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <ModalChangePassword />
                </div>
              </div>

              {/* Withdrawal Password */}
              <div className="p-3 transition-colors bg-gray-50 rounded-xl hover:bg-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FaKey className="text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <div className="mb-1 text-xs text-gray-500">{t('matkhauruttien')}</div>
                    <div className="text-sm font-semibold text-gray-800">***********</div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <ModalPasswordV2 />
                </div>
              </div>
            </div>
          </Card>

          {/* Bank Information Card */}
          <Card
            className="transition-all duration-300 border-0 shadow-xl rounded-2xl bg-white/90 backdrop-blur-sm hover:shadow-2xl"
            title={
              <div className="flex items-center gap-3 text-lg font-bold text-gray-800">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <FaUniversity className="text-indigo-600" />
                </div>
                {t('nganhang')}
              </div>
            }>
            {agency?.bank_name ? (
              <div className="space-y-4">
                {/* Bank Name */}
                <div className="flex items-center gap-3 p-3 transition-colors bg-gray-50 rounded-xl hover:bg-gray-100">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <FaUniversity className="text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <div className="mb-1 text-xs text-gray-500">{t('tennganhang')}</div>
                    <div className="text-sm font-semibold text-gray-800">{agency?.bank_name}</div>
                  </div>
                </div>

                {/* Account Number */}
                <div className="flex items-center gap-3 p-3 transition-colors bg-gray-50 rounded-xl hover:bg-gray-100">
                  <div className="p-2 bg-pink-100 rounded-lg">
                    <FaCreditCard className="text-pink-600" />
                  </div>
                  <div className="flex-1">
                    <div className="mb-1 text-xs text-gray-500">{t('stk')}</div>
                    <div className="font-mono text-sm font-semibold text-gray-800">
                      {agency?.bank_number}
                    </div>
                  </div>
                </div>

                {/* Account Holder */}
                <div className="flex items-center gap-3 p-3 transition-colors bg-gray-50 rounded-xl hover:bg-gray-100">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <FaUserEdit className="text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <div className="mb-1 text-xs text-gray-500">{t('tenchutk')}</div>
                    <div className="text-sm font-semibold text-gray-800">{agency?.bank_user}</div>
                  </div>
                </div>

                {/* Edit Bank Info */}
                <div className="flex justify-center pt-2">
                  <Button
                    className="px-4 py-1 text-sm font-semibold text-white transition-all duration-300 border-none shadow-lg bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl hover:scale-105 hover:shadow-xl"
                    icon={<FaEdit />}
                    onClick={() => setIsOpen(true)}>
                    {t('cap_nhat')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-4 text-center">
                <div className="mb-4">
                  <div className="flex items-center justify-center w-16 h-16 p-3 mx-auto mb-3 bg-yellow-100 rounded-full">
                    <FaExclamationTriangle className="text-xl text-yellow-600" />
                  </div>
                  <h4 className="mb-2 text-lg font-semibold text-gray-800">
                    {t('chuacotknganhang')}
                  </h4>
                  <p className="mb-4 text-sm text-gray-600">
                    {t('vui_long_them_thong_tin_ngan_hang')}
                  </p>
                </div>
                <div className="flex justify-center">
                  <Button
                    className="flex items-center justify-center gap-2 px-4 py-1 text-sm font-semibold text-white transition-all duration-300 border-none shadow-lg bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl hover:scale-105 hover:shadow-x"
                    icon={<FaPlus />}
                    onClick={() => setIsOpen(true)}>
                    {t('themnganhang')}
                  </Button>
                </div>
              </div>
            )}
            <ModalChangeBank isOpen={isOpen} setIsOpen={setIsOpen} />
          </Card>
        </div>
      </div>

      {false && (
        <style jsx>{`
          /* Custom card styling */
          .ant-card {
            border-radius: 16px !important;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1) !important;
          }

          .ant-card-head {
            border-bottom: 1px solid #f0f0f0 !important;
            padding: 20px 24px !important;
          }

          .ant-card-body {
            padding: 24px !important;
          }

          .ant-card-head-title {
            font-size: 18px !important;
            font-weight: 600 !important;
          }

          /* Enhanced hover effects */
          .ant-card:hover {
            transform: translateY(-4px) !important;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
          }

          /* Button styling */
          .ant-btn:hover {
            transform: translateY(-1px) !important;
          }

          /* Avatar styling */
          .ant-avatar {
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
          }

          /* Tag styling */
          .ant-tag {
            border-radius: 8px !important;
            padding: 4px 12px !important;
            font-weight: 500 !important;
            border: none !important;
          }

          /* Mobile responsiveness */
          @media (max-width: 1024px) {
            .grid.grid-cols-1.lg\\:grid-cols-3 {
              grid-template-columns: 1fr !important;
            }
          }

          @media (max-width: 768px) {
            .max-w-4xl {
              max-width: 100% !important;
              padding: 0 16px !important;
            }
          }

          /* Smooth transitions for all elements */
          * {
            transition: all 0.3s ease !important;
          }

          /* Custom scrollbar */
          ::-webkit-scrollbar {
            width: 8px;
          }

          ::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 4px;
          }

          ::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, #f6a04d 0%, #6366f1 100%);
            border-radius: 4px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(135deg, #2563eb 0%, #4f46e5 100%);
          }
        `}</style>
      )}
    </div>
  );
};

export default Profile;
